import { supabase } from './SupabaseConfig';
import { IdentityRepository, ProfileData } from '../../domain/identity/ports/IdentityRepository';
import { UserRole } from '../../domain/identity/entities/UserRole';
import { USER_ROLE } from '@core/constants';

export class SupabaseIdentityRepository implements IdentityRepository {
  async getProfile(uid: string): Promise<ProfileData | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, must_change_password')
        .eq('id', uid)
        .maybeSingle();
      
      if (error || !data) return null;
      
      return {
        uid: data.id,
        fullName: data.full_name,
        mustChangePassword: !!data.must_change_password
      };
    } catch (err) {
      console.error('[SupabaseIdentityRepo] getProfile failed:', err);
      return null;
    }
  }

  async getRole(uid: string): Promise<UserRole> {
    try {
      // 1. Check Master
      const { data: masterData } = await supabase
        .from('system_admins')
        .select('active')
        .eq('user_id', uid)
        .maybeSingle();
      
      if (masterData?.active) {
        return new UserRole({ role: 'master' as any });
      }

      // 2. Check Owner
      const { data: ownerData } = await supabase
        .from('owners')
        .select('id, is_active')
        .eq('id', uid)
        .maybeSingle();
      
      if (ownerData?.is_active) {
        return new UserRole({ role: USER_ROLE.OWNER, ownerId: ownerData.id });
      }

      // 3. Check Staff
      const { data: staffData } = await supabase
        .from('staff_assignments')
        .select('role, store_id, stores(owner_id)')
        .eq('user_id', uid)
        .maybeSingle();
      
      if (staffData) {
        return new UserRole({
          role: 'staff' as any,
          ownerId: (staffData.stores as any).owner_id,
          assignedLocationId: staffData.store_id,
          userRole: UserRole.mapToUserRole(staffData.role)
        });
      }

      return new UserRole({ role: null });
    } catch (err) {
      console.error('[SupabaseIdentityRepo] getRole failed:', err);
      return new UserRole({ role: null });
    }
  }

  async updateProfile(uid: string, data: Partial<ProfileData>): Promise<void> {
    const updateData: any = {};
    if (data.fullName !== undefined) updateData.full_name = data.fullName;
    if (data.mustChangePassword !== undefined) updateData.must_change_password = data.mustChangePassword;

    const { error } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', uid);
    
    if (error) throw error;
  }

  async getStaffAssignment(uid: string): Promise<{ role: string; storeId: string; ownerId: string; } | null> {
    const { data, error } = await supabase
      .from('staff_assignments')
      .select('role, store_id, stores(owner_id)')
      .eq('user_id', uid)
      .maybeSingle();
    
    if (error || !data) return null;

    return {
      role: data.role,
      storeId: data.store_id,
      ownerId: (data.stores as any).owner_id
    };
  }

  async assignStaff(storeId: string, userId: string, role: string): Promise<void> {
    const { error } = await supabase
      .from('staff_assignments')
      .upsert({
        user_id: userId,
        store_id: storeId,
        role: role,
        assigned_at: new Date().toISOString()
      }, { onConflict: 'user_id,store_id' });
    
    if (error) throw error;
  }

  async removeStaff(storeId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('staff_assignments')
      .delete()
      .eq('user_id', userId)
      .eq('store_id', storeId);
    
    if (error) throw error;
  }
  
  async createOwnerProfile(user: any): Promise<void> {
    if (!user?.id) throw new Error('Người dùng chưa xác thực');

    let brandName = 'My Brand';
    let tier = 'BASIC';
    let maxStores = 2;

    // 1. Check for provision invite
    try {
      const { data: invite } = await supabase
        .from('provision_invites')
        .select('*')
        .eq('email', user.email)
        .maybeSingle();

      if (invite) {
        brandName = invite.brand_name;
        tier = invite.tier;
        maxStores = invite.max_stores;
        
        // 2. Delete the invite (consumption)
        await supabase.from('provision_invites').delete().eq('email', user.email);
      }
    } catch (err: any) {
      console.log('[SupabaseIdentityRepo] No pending invite found:', err.message);
    }

    // 3. Create owner entry
    const { error } = await supabase
      .from('owners')
      .insert({
        id: user.id,
        brand_name: brandName,
        tier: tier,
        max_stores: maxStores,
        is_active: true
      });
    
    if (error) {
      console.error('[SupabaseIdentityRepo] createOwnerProfile failed:', error);
      throw error;
    }
  }

  // Security Settings Implementation
  async getSecuritySettings(uid: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('user_security_settings')
        .select('*')
        .eq('user_id', uid)
        .maybeSingle();

      if (error) throw error;
      if (!data) return { adminPin: '', mfaEnabled: false, devices: [] };

      return {
        adminPin: data.admin_pin || '',
        mfaEnabled: !!data.mfa_enabled,
        devices: data.devices || []
      };
    } catch (err) {
      console.error('[SupabaseIdentityRepo] getSecuritySettings failed:', err);
      return { adminPin: '', mfaEnabled: false, devices: [] };
    }
  }

  async saveSecuritySettings(uid: string, settings: any): Promise<void> {
    try {
      const payload: any = {
        user_id: uid,
        updated_at: new Date().toISOString()
      };

      if (settings.adminPin !== undefined) payload.admin_pin = settings.adminPin;
      if (settings.mfaEnabled !== undefined) payload.mfa_enabled = settings.mfaEnabled;
      if (settings.devices !== undefined) payload.devices = settings.devices;

      const { error } = await supabase
        .from('user_security_settings')
        .upsert(payload, { onConflict: 'user_id' });

      if (error) throw error;
    } catch (err) {
      console.error('[SupabaseIdentityRepo] saveSecuritySettings failed:', err);
      throw err;
    }
  }
}
